import Link from 'next/link';
import React from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import {
  DISCORD_LINK,
  INSTAGRAM_LINK,
  TWITTER_LINK,
  TELEGRAM_LINK,
  MC_LINK,
  HANA_STUDIOS_LINK,
  CLOUT_CHASE_LINK,
  HANA_LINK,
  UNAGI_GAMES_LINK,
  GIPHY_LINK,
  UNA_FLEX_LINK,
  BRAND_ASSETS_LINK,
  IMAGINE_LINK,
} from '@web/features/const';
import {
  CloutChaseIcon,
  HanaIcon,
  ImagineIcon,
  MangaIcon,
  McIcon,
  ProgramIcon,
  StudioIcon,
  UnaIcon,
} from '@web/features/hub-rework/brand-icons';
import { env } from '@web/utils/env';

const FooterContainer = styled.footer<{ $compact?: boolean }>`
  box-sizing: border-box;
  display: grid;
  height: ${({ $compact }) => ($compact ? 'auto' : '600px')};
  padding: ${({ theme }) => theme.spacing('3000')};
  row-gap: ${({ theme }) => theme.spacing('1500')};
  column-gap: ${({ theme }) => theme.spacing('1500')};
  align-self: stretch;
  grid-template-rows: ${({ $compact }) =>
    $compact ? 'auto auto' : 'minmax(0, 1fr) 16px'};
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid ${({ theme }) => theme.color.outlineVariant};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 1800px;
    margin-inline: auto;
    width: 100%;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: flex;
    padding: ${({ theme }) => theme.spacing('2000')};
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing('3000')};
    align-self: stretch;
  }
`;

const EcosystemContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  grid-row: 1 / span 1;
  grid-column: 1 / span 2;
  gap: ${({ theme }) => theme.spacing('2000')};
`;

const ColumnTitle = styled.h3`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['1']};
`;

const EcosystemBrandsContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  align-self: stretch;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing('2000')};
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
`;

const EcosystemBrandItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    text-decoration: underline;
  }
  > svg {
    color: ${({ theme }) => theme.color.onBackground};
  }
`;

const Links = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1 0 0;
  align-self: stretch;
  grid-row: 1 / span 1;
  grid-column: ${({ $compact }) => ($compact ? '1 / span 1' : '3 / span 1')};
`;

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  gap: ${({ theme }) => theme.spacing('2000')};
  a {
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['0875']};
    color: ${({ theme }) => theme.color.onBackground};
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SubFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  flex: 1 0 0;
  align-self: stretch;
  flex-wrap: wrap;
  grid-row: 2 / span 1;
  grid-column: 1 / span 3;
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  color: ${({ theme }) => theme.color.onBackground};

  ${({ theme }) => theme.breakpoints.down('md')} {
    align-items: flex-start;
    align-content: flex-start;
    gap: ${({ theme }) => theme.spacing('1000')};
    align-self: stretch;
    flex-wrap: wrap;
  }
`;

const SubFooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('2000')};

  a {
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Footer = () => {
  const t = useTranslations();
  return (
    <FooterContainer $compact={env.SHELL_MODE}>
      {/* The ecosystem strip linked out to sibling brands that are no longer
          maintained; it stays in the tree for a future rebuild. */}
      {!env.SHELL_MODE && (
        <EcosystemContainer>
          <ColumnTitle>{t('HUB_FOOTER_ECOSYSTEM_TITLE')}</ColumnTitle>
          <EcosystemBrandsContainer>
            <EcosystemBrandItem
              href={UNAGI_GAMES_LINK}
              target="_blank"
              rel="noreferrer"
            >
              <UnaIcon size="2500" />
              <span>{t('HUB_FOOTER_UNA')}</span>
            </EcosystemBrandItem>

            <EcosystemBrandItem href={MC_LINK} target="_blank" rel="noreferrer">
              <McIcon size="2500" />
              <span>{t('HUB_FOOTER_MC')}</span>
            </EcosystemBrandItem>

            <EcosystemBrandItem href="/manga">
              <MangaIcon size="2500" />
              <span>{t('HUB_FOOTER_SHARDS')}</span>
            </EcosystemBrandItem>

            <EcosystemBrandItem
              href={HANA_STUDIOS_LINK}
              target="_blank"
              rel="noreferrer"
            >
              <StudioIcon size="2500" />
              <span>{t('HUB_FOOTER_HANA_STUDIOS')}</span>
            </EcosystemBrandItem>

            <EcosystemBrandItem
              href={CLOUT_CHASE_LINK}
              target="_blank"
              rel="noreferrer"
            >
              <CloutChaseIcon size="2500" />
              <span>{t('HUB_FOOTER_CLOUT_CHASE')}</span>
            </EcosystemBrandItem>

            <EcosystemBrandItem href={HANA_LINK} target="_blank" rel="noreferrer">
              <HanaIcon size="2500" />
              <span>{t('HUB_FOOTER_HANA')}</span>
            </EcosystemBrandItem>

            {!env.SHELL_MODE && (
              <EcosystemBrandItem href="/program">
                <ProgramIcon size="2500" />
                <span>{t('HUB_FOOTER_PROGRAM')}</span>
              </EcosystemBrandItem>
            )}

            <EcosystemBrandItem
              href={IMAGINE_LINK}
              target="_blank"
              rel="noreferrer"
            >
              <ImagineIcon size="2500" />
              <span>{t('HUB_FOOTER_IMAGINE')}</span>
            </EcosystemBrandItem>
          </EcosystemBrandsContainer>
        </EcosystemContainer>
      )}

      <Links $compact={env.SHELL_MODE}>
        <LinksColumn>
          <ColumnTitle>{t('W_PERSONA')}</ColumnTitle>
          <Link href="/">{t('W_HOME')}</Link>
          <Link href="/collection">{t('W_COLLECTION')}</Link>
          {/* Program, Staking and Settings are the account surfaces; the
              public build does not carry an account layer, so it does not
              link to them. */}
          {!env.SHELL_MODE && (
            <>
              <Link href="/program">{t('W_PROGRAM')}</Link>
              <Link href="/staking">{t('W_STAKING')}</Link>
            </>
          )}
          <Link href="/lore">{t('HUB_LORE_TAB')}</Link>
          <Link href="/manga">{t('W_MANGA')}</Link>
          <Link href={UNA_FLEX_LINK} target="_blank" rel="noreferrer">
            {t('W_UNA_FLEX')}
          </Link>
          {!env.SHELL_MODE && (
            <Link href="/settings">{t('W_SETTINGS')}</Link>
          )}
          <Link href={BRAND_ASSETS_LINK}>{t('W_BRAND_ASSETS')}</Link>
        </LinksColumn>

        <LinksColumn>
          <ColumnTitle>{t('HUB_FOOTER_CONNECT_TITLE')}</ColumnTitle>
          <Link href={TWITTER_LINK} target="_blank" rel="noreferrer">
            {t('W_TWITTER')}
          </Link>
          {!env.SHELL_MODE && (
            <Link href={DISCORD_LINK} target="_blank" rel="noreferrer">
              {t('W_DISCORD')}
            </Link>
          )}
          <Link href={TELEGRAM_LINK} target="_blank" rel="noreferrer">
            {t('W_TELEGRAM')}
          </Link>
          <Link href={INSTAGRAM_LINK} target="_blank" rel="noreferrer">
            {t('W_INSTAGRAM')}
          </Link>
          <Link href={GIPHY_LINK} target="_blank" rel="noreferrer">
            {t('W_GIPHY')}
          </Link>
        </LinksColumn>
      </Links>

      <SubFooter>
        <span>{t('HUB_SUB_FOOTER_TITLE')}</span>
        <SubFooterLinks>
          <Link href="/privacy">{t('HUB_SUB_FOOTER_PRIVACY')}</Link>
          <Link href="/terms">{t('HUB_SUB_FOOTER_TERMS')}</Link>
        </SubFooterLinks>
      </SubFooter>
    </FooterContainer>
  );
};
